import * as Sentry from '@sentry/react';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private formatMessage(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    if (context && Object.keys(context).length > 0) {
      return `${prefix} ${message} ${JSON.stringify(context, null, 2)}`;
    }

    return `${prefix} ${message}`;
  }

  private logToConsole(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    if (import.meta.env.MODE === 'production') {
      return;
    }
    const formattedMessage = this.formatMessage(level, message, context);

    switch (level) {
      case 'debug':
        console.debug(formattedMessage);
        break;
      case 'info':
        console.info(formattedMessage);
        break;
      case 'warn':
        console.warn(formattedMessage);
        break;
      case 'error':
        console.error(formattedMessage);
        break;
    }
  }

  private logToSentry(
    level: LogLevel,
    message: string,
    context?: LogContext
  ): void {
    switch (level) {
      case 'debug':
        Sentry.logger.debug(message, { ...context });
        break;
      case 'info':
        Sentry.logger.info(message, { ...context });
        break;
      case 'warn':
        Sentry.logger.warn(message, { ...context });
        break;
      case 'error':
        Sentry.logger.error(message, { ...context });
        break;
    }
  }

  debug(message: string, context?: LogContext): void {
    this.logToSentry('debug', message, context);
    this.logToConsole('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.logToSentry('info', message, context);
    this.logToConsole('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.logToSentry('warn', message, context);
    this.logToConsole('warn', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.logToSentry('error', message, context);
    this.logToConsole('error', message, context);
  }

  // Convenience methods for common use cases
  logUserAction(action: string, context?: LogContext): void {
    this.info(`User action: ${action}`, { ...context, type: 'user-action' });
  }

  logApiCall(
    method: string,
    endpoint: string,
    status?: number,
    context?: LogContext
  ): void {
    const level = status && status >= 400 ? 'error' : 'info';
    this[level](`API call: ${method} ${endpoint}`, {
      ...context,
      method,
      endpoint,
      status,
      type: 'api-call',
    });
  }

  logWSEvent(namespace: string, event: string, context?: LogContext): void {
    this.info(`Websocket event [${namespace}]: ${event}`, {
      ...context,
      event,
      type: 'ws-event',
    });
  }

  logWSError(message: string, context?: LogContext): void {
    this.info(`Websocket Error: ${message}`, {
      ...context,
      type: 'ws-error',
    });
  }

  logNavigation(route: string, context?: LogContext): void {
    this.info(`Navigation: ${route}`, {
      ...context,
      route,
      type: 'navigation',
    });
  }

  logPerformance(
    operation: string,
    duration: number,
    context?: LogContext
  ): void {
    this.info(`Performance: ${operation}`, {
      ...context,
      operation,
      duration,
      type: 'performance',
    });
  }

  logQuery(
    queryKey: string[],
    status: 'success' | 'error',
    context?: LogContext
  ): void {
    const level = status === 'error' ? 'error' : 'debug';
    this[level](`Query ${status}: ${queryKey.join('.')}`, {
      ...context,
      queryKey,
      status,
      type: 'query',
    });
  }

  logMutation(
    mutationKey: string[],
    status: 'success' | 'error',
    context?: LogContext
  ): void {
    const level = status === 'error' ? 'error' : 'debug';
    this[level](`Mutation ${status}: ${mutationKey.join('.')}`, {
      ...context,
      mutationKey,
      status,
      type: 'mutation',
    });
  }

  // Method to create a scoped logger with default context
  createScopedLogger(defaultContext: LogContext): Logger {
    const scopedLogger = new Logger();

    // Override methods to include default context
    const originalMethods = ['debug', 'info', 'warn', 'error'] as const;

    originalMethods.forEach((method) => {
      const originalMethod = scopedLogger[method].bind(scopedLogger);
      scopedLogger[method] = (message: string, context?: LogContext) => {
        originalMethod(message, { ...defaultContext, ...context });
      };
    });

    return scopedLogger;
  }
}

// Export a singleton instance
export const logger = new Logger();

// Export the class for creating custom instances
export { Logger };

// Export types for use in other files
export type { LogContext, LogLevel };
